import { DocumentStatus } from 'constant';
import type { ApplicationDocumentInterface } from 'types';

export const isAllDocumentsCompleted = (documents: ApplicationDocumentInterface[]) => {
    return documents.every((doc) => doc.status === DocumentStatus.APPROVED);
};

/** Minimal shape shared by the various document types that carry a description. */
interface DescribableDocument {
    explanation?: string | null;
    description_en?: string | null;
    description_fr?: string | null;
}

const TEMPLATE_URL_RE = /https?:\/\/[^\s<>"')]+/i;

/**
 * Resolve a document's description/explanation HTML, preferring the field that
 * matches the given language and falling back to the other localized field,
 * then the raw explanation.
 */
export const getDocumentDescriptionHtml = (
    doc: DescribableDocument,
    language?: string
): string | null => {
    const localized =
        language?.toUpperCase() === 'FR'
            ? doc.description_fr || doc.description_en
            : doc.description_en || doc.description_fr;
    return localized || doc.explanation || null;
};

/** True when the resolved description has visible text, not only empty markup. */
export const hasDocumentDescription = (doc: DescribableDocument, language?: string): boolean => {
    const html = getDocumentDescriptionHtml(doc, language);
    if (!html) return false;

    const text = html
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    return text.length > 0;
};

/**
 * Extract the first http(s) URL embedded in a document's description/explanation.
 * Used for "template from URL" documents that link out instead of storing a file.
 * Returns null when there is no URL. Only the first match is returned.
 */
export const getExplanationUrl = (doc: DescribableDocument, language?: string): string | null => {
    const rawHtml = getDocumentDescriptionHtml(doc, language);
    if (!rawHtml) return null;
    const match = rawHtml.match(TEMPLATE_URL_RE);
    return match ? match[0].replace(/[.,;:!?)]+$/, '') : null;
};

const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;
// Punctuation that usually belongs to the sentence, not the URL (e.g. "see https://x.ca).").
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

const toAbsoluteUrl = (url: string) =>
    url.toLowerCase().startsWith('www.') ? `https://${url}` : url;

/**
 * Converts plain-text URLs in a description's HTML into anchors (skipping text
 * already inside <a> tags) and forces every link to open in a new tab. Returns
 * HTML safe to render via dangerouslySetInnerHTML alongside sanitizeHtml.
 */
export const linkifyHtml = (html: string): string => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    while (walker.nextNode()) {
        const node = walker.currentNode as Text;
        if (!node.parentElement?.closest('a')) {
            textNodes.push(node);
        }
    }

    textNodes.forEach((node) => {
        const text = node.textContent ?? '';
        URL_PATTERN.lastIndex = 0;
        if (!URL_PATTERN.test(text)) return;

        const fragment = doc.createDocumentFragment();
        let cursor = 0;
        URL_PATTERN.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = URL_PATTERN.exec(text)) !== null) {
            const url = match[0].replace(TRAILING_PUNCTUATION, '');
            if (!url) continue;
            if (match.index > cursor) {
                fragment.appendChild(doc.createTextNode(text.slice(cursor, match.index)));
            }
            const anchor = doc.createElement('a');
            anchor.href = toAbsoluteUrl(url);
            anchor.textContent = url;
            fragment.appendChild(anchor);
            cursor = match.index + url.length;
        }
        if (cursor < text.length) {
            fragment.appendChild(doc.createTextNode(text.slice(cursor)));
        }
        node.replaceWith(fragment);
    });

    doc.body.querySelectorAll('a').forEach((anchor) => {
        const href = anchor.getAttribute('href') ?? '';
        anchor.setAttribute('href', toAbsoluteUrl(href));
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
    });

    return doc.body.innerHTML;
};
